import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, catchError, of, map } from 'rxjs';
import { LoginRequest } from '../models/auth.interface';
import { AuthUserDto } from '../models/user.interface';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<AuthUserDto | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private isInitialized = false;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr) as AuthUserDto;
        this.currentUserSubject.next(user);
        this.isInitialized = true;
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        localStorage.removeItem('user');
        this.isInitialized = true;
      }
    } else {
      this.isInitialized = true;
    }
  }

  // Método para inicializar y verificar la sesión con el servidor
  initializeAuth(): Observable<boolean> {
    if (!this.currentUserSubject.value) {
      return of(true); // No hay usuario, no necesita verificación
    }

    return this.refreshUserData().pipe(
      map((user: AuthUserDto | null) => {
        this.isInitialized = true;
        return user !== null;
      }),
      catchError(() => {
        this.isInitialized = true;
        return of(false);
      })
    );
  }

  login(credentials: LoginRequest): Observable<AuthUserDto> {
    return this.http.post<AuthUserDto>(`${this.apiUrl}/api/auth/login`, credentials, {
      withCredentials: true // Importante para que las cookies se envíen
    }).pipe(
      tap(user => {
        this.setCurrentUser(user);
      }),
      catchError(error => {
        console.error('Login error:', error);
        throw error;
      })
    );
  }

  logout(): void {
    // Llamar al endpoint de logout si existe, o simplemente limpiar el estado local
    this.http.post(`${this.apiUrl}/api/auth/logout`, {}, { 
      withCredentials: true 
    }).subscribe({
      complete: () => {
        this.clearUserSession();
      },
      error: () => {
        // Aunque el endpoint falle, limpiar sesión local
        this.clearUserSession();
      }
    });
  }

  private clearUserSession(): void {
    localStorage.removeItem('user');
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth']);
  }

  isAuthenticated(): boolean {
    // Con cookies HttpOnly, verificamos si tenemos datos del usuario
    // La autenticación real se verifica en el servidor
    return this.currentUserSubject.value !== null;
  }

  get authInitialized(): boolean {
    return this.isInitialized;
  }

  getCurrentUser(): AuthUserDto | null {
    return this.currentUserSubject.value;
  }

  getUserRole(): number | null {
    const user = this.getCurrentUser();
    if (!user || !user.roles || user.roles.length === 0) {
      return null;
    }
    
    // Mapear roles de string a números
    const roleMapping: { [key: string]: number } = {
      'admin': 1,
      'administrador': 1,
      'user': 2,
      'usuario': 2,
      'super admin': 3,      // Super Administrador con espacio
      'superadmin': 3,       // Super Administrador sin espacio
      'super administrador': 3,  // Variante en español
      'superadministrador': 3    // Variante en español sin espacio
    };

    const originalRole = user.roles[0];
    const roleName = originalRole.toLowerCase().trim(); // Tomar el primer rol, limpiar espacios
    const roleId = roleMapping[roleName];
    
    if (!roleId) {
      console.warn(`⚠️ Rol desconocido: "${originalRole}" (normalizado: "${roleName}"). Mapeando a User (2)`);
      console.warn('Roles disponibles en mapeo:', Object.keys(roleMapping));
      return 2; // Default a User (2)
    }
    
    return roleId;
  }

  hasRole(roleId: number): boolean {
    const userRoleId = this.getUserRole();
    return userRoleId === roleId;
  }

  canAccess(requiredRoles: number[]): boolean {
    const userRoleId = this.getUserRole();
    return userRoleId !== null && requiredRoles.includes(userRoleId);
  }

  private setCurrentUser(user: AuthUserDto): void {
    localStorage.setItem('user', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  // Método para refrescar los datos del usuario
  refreshUserData(): Observable<AuthUserDto | null> {
    return this.http.get<AuthUserDto>(`${this.apiUrl}/api/auth/me`, {
      withCredentials: true
    }).pipe(
      tap(user => {
        this.setCurrentUser(user);
      }),
      catchError((error) => {
        console.error('Error refreshing user data:', error);
        if (error.status === 401) {
          this.clearUserSession();
        }
        return of(null);
      })
    );
  }
}
